/**
 * DeviceLimitPage
 *
 * Shown when a user tries to log in but has already reached the maximum
 * number of concurrent sessions (2). Mirrors the Hotstar "Login Pending,
 * Device Limit Reached" experience.
 *
 * Uses BroadcastChannel to detect when another tab logs out — no polling.
 * The user can also manually refresh the list or force-logout a session here.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts';
import {
  useLoginMutation,
  usePublicLogoutSessionMutation,
  usePublicGetSessionsMutation,
  useGetPendingSsoSessionsMutation,
  useCompletePendingSsoLoginMutation,
  useReplacePendingSsoSessionMutation,
  useGetAuthConfigurationQuery,
} from '../../store';
import { IActiveSession } from '../../store/rtkQuery/auth';
import { setChatbotToken } from '../../utils';
import { saveAssetToken } from '../../services/knowledgeHub';
import { TurnstileCaptcha, TurnstileCaptchaRef } from '../../components';

// ─── Device icon helpers ──────────────────────────────────────────────────────

function DeviceIcon({ label }: { label: string }) {
  const l = label.toLowerCase();

  if (l.includes('tv')) {
    return (
      <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <rect x="2" y="3" width="20" height="14" rx="2" strokeWidth="1.8" />
        <path d="M8 21h8M12 17v4" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }

  if (
    l.includes('android') || l.includes('iphone') || l.includes('mobile') ||
    l.includes('phone') || l.includes('tablet') || l.includes('ipad')
  ) {
    return (
      <svg className="w-6 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <rect x="5" y="2" width="14" height="20" rx="2" strokeWidth="1.8" />
        <circle cx="12" cy="18" r="1" fill="currentColor" />
      </svg>
    );
  }

  return (
    <svg className="w-7 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <rect x="2" y="4" width="20" height="13" rx="2" strokeWidth="1.8" />
      <path d="M0 21h24" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M8 21l2-4h4l2 4" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Relative time helper ─────────────────────────────────────────────────────

function relativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
}

/** One row per session id — avoids duplicate keys / inflated counts if API overlaps. */
function dedupeSessionsBySessionId(sessions: IActiveSession[]): IActiveSession[] {
  const seen = new Set<string>();
  const out: IActiveSession[] = [];
  for (const s of sessions) {
    const id = s.sessionId;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(s);
  }
  return out;
}

function isAuthEncryptedPassword(value?: string): value is string {
  if (!value) return false;

  const [ivBase64, encryptedBase64, ...extra] = value.split(':');
  if (extra.length || !ivBase64 || !encryptedBase64) return false;

  try {
    return atob(ivBase64).length === 16 && atob(encryptedBase64).length > 0;
  } catch {
    return false;
  }
}

function buildPublicSessionAuthPayload(email: string, password?: string) {
  if (password === undefined) return { email };
  if (!isAuthEncryptedPassword(password)) {
    throw new Error('Invalid encrypted password');
  }
  return { email, password };
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function DeviceLimitPage() {
  const navigate = useNavigate();
  const isSsoPending =
    new URLSearchParams(window.location.search).get('method') === 'sso';
  const {
    sessionLimitData,
    setSessionLimitData,
    clearSessionLimitData,
    setIsAuthenticated,
    setUser,
    setAuthenticationMethod,
  } = useAuth();

  const [publicLogoutSession, { isLoading: isLoggingOut }] = usePublicLogoutSessionMutation();
  const [publicGetSessions] = usePublicGetSessionsMutation();
  const [loginUser, { isLoading: isLoggingIn }] = useLoginMutation();
  const [getPendingSsoSessions] = useGetPendingSsoSessionsMutation();
  const [completePendingSsoLogin] = useCompletePendingSsoLoginMutation();
  const [replacePendingSsoSession, { isLoading: isReplacingSsoSession }] =
    useReplacePendingSsoSessionMutation();
  const { data: authConfiguration } = useGetAuthConfigurationQuery();
  const captchaEnabled = Boolean(authConfiguration?.data?.captchaEnabled);

  const captchaRef = useRef<TurnstileCaptchaRef>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaTokenRef = useRef<string | null>(null);
  useEffect(() => {
    captchaTokenRef.current = captchaToken;
  }, [captchaToken]);

  const handleCaptchaVerify = useCallback((token: string) => {
    setCaptchaToken(token);
    setError(null);
  }, []);

  const handleCaptchaExpire = useCallback(() => {
    setCaptchaToken(null);
  }, []);

  const handleCaptchaError = useCallback(() => {
    setCaptchaToken(null);
    setError('Security verification failed. Please try again.');
  }, []);

  const [ssoSessions, setSsoSessions] = useState<IActiveSession[]>([]);
  const [ssoMaxSessions, setSsoMaxSessions] = useState(2);

  const [loadingSessionId, setLoadingSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Ref so the polling callback always reads the latest sessionLimitData
  // without needing it as a dependency (avoids restarting the interval on
  // every state update).
  const sessionLimitDataRef = useRef(sessionLimitData);
  useEffect(() => {
    sessionLimitDataRef.current = sessionLimitData;
  }, [sessionLimitData]);

  // Ref to track whether we're mid-login to avoid concurrent attempts
  const isLoggingInRef = useRef(false);
  // Prevent the missing-data route guard from racing with an intentional
  // navigation after login, OTP fallback, or "Back to sign in".
  const isCompletingFlowRef = useRef(false);

  /**
   * Monotonic id for in-flight session-list fetches. When multiple
   * checkAndLogin() runs overlap (mount refresh + logout + BroadcastChannel),
   * an older HTTP response must not overwrite newer state — that stale write
   * is what kept the device list stuck showing too many sessions after logout.
   */
  const sessionsFetchIdRef = useRef(0);

  /**
   * Core refresh logic. Fetches the live session list and, if a slot has
   * opened up, logs the user in automatically.
   *
   * Returns true if the user was logged in (caller should stop polling).
   */
  const checkAndLogin = useCallback(async (): Promise<boolean> => {
    if (isSsoPending) {
      try {
        const response = await getPendingSsoSessions().unwrap();
        const sessions = dedupeSessionsBySessionId(
          response.data.activeSessions ?? [],
        );
        setSsoSessions(sessions);
        setSsoMaxSessions(response.data.maxSessions);
        if (sessions.length < response.data.maxSessions) {
          const completion = await completePendingSsoLogin().unwrap();
          if (completion.data.completed && completion.data.redirectUrl) {
            window.location.assign(completion.data.redirectUrl);
            return true;
          }
        }
      } catch (err: any) {
        setError(
          err?.data?.message ||
            'Your pending SSO login expired. Please return to sign in.',
        );
      }
      return false;
    }

    const data = sessionLimitDataRef.current;
    if (!data) return false;
    if (isLoggingInRef.current) return false;

    const fetchId = ++sessionsFetchIdRef.current;

    const { email, password, maxSessions } = data;
    const hasPasswordAuth = isAuthEncryptedPassword(password);

    try {
      const authPayload = buildPublicSessionAuthPayload(email, password);
      const result = await publicGetSessions(authPayload).unwrap();

      // Drop stale responses so an older in-flight request cannot clobber UI
      if (fetchId !== sessionsFetchIdRef.current) {
        return false;
      }

      const payload = result.data;
      const rawSessions = payload?.activeSessions ?? [];
      const limit = payload?.maxSessions ?? maxSessions;
      const fresh = dedupeSessionsBySessionId(rawSessions);

      // Update the displayed list regardless
      setSessionLimitData(prev =>
        prev ? { ...prev, activeSessions: fresh, maxSessions: limit } : prev,
      );

      if (fresh.length < limit) {
        // Another fetch may have started while we awaited — don't auto-login on stale data
        if (fetchId !== sessionsFetchIdRef.current) {
          return false;
        }

        isLoggingInRef.current = true;
        try {
          if (hasPasswordAuth) {
            const tokenToSend = captchaTokenRef.current;
            if (captchaEnabled && !tokenToSend) {
              isLoggingInRef.current = false;
              return false;
            }
            const response = await loginUser({
              email,
              password,
              ...(tokenToSend ? { captchaToken: tokenToSend } : {}),
            }).unwrap();
            isCompletingFlowRef.current = true;
            setIsAuthenticated(true);
            setUser(response?.data?.user);
            setAuthenticationMethod('LEGACY_PASSWORD');
            setChatbotToken(response?.data?.chatbotToken);
            saveAssetToken(response?.data?.assetToken);
            navigate('/', { replace: true });
            clearSessionLimitData();
          } else {
            isCompletingFlowRef.current = true;
            navigate('/login-with-otp', { replace: true });
            clearSessionLimitData();
          }
          return true;
        } catch (err: any) {
          isLoggingInRef.current = false;
          captchaRef.current?.reset();
          setCaptchaToken(null);
          captchaTokenRef.current = null;
          setError(
            err?.data?.message ||
              'A session slot opened, but sign-in could not be completed. Please try again.',
          );
          return false;
        }
      }
    } catch (err: any) {
      if (err?.message) {
        setError(err.message);
      }
      // Silently ignore poll errors — don't show error for background checks
    }
    return false;
  }, [
    publicGetSessions,
    loginUser,
    setSessionLimitData,
    clearSessionLimitData,
    setIsAuthenticated,
    setUser,
    setAuthenticationMethod,
    navigate,
    isSsoPending,
    getPendingSsoSessions,
    completePendingSsoLogin,
    captchaEnabled,
  ]);

  /**
   * Manual refresh — same logic but shows the spinner and surfaces errors.
   */
  async function refreshSessions() {
    setIsRefreshing(true);
    setError(null);
    try {
      await checkAndLogin();
    } catch (err: any) {
      setError(err?.data?.message || 'Failed to refresh session list.');
    } finally {
      setIsRefreshing(false);
    }
  }

  // On mount: single immediate check + listen for logout events from other tabs
  useEffect(() => {
    if (!sessionLimitData && !isSsoPending) return;

    // Immediate check on mount to get fresh session list
    checkAndLogin();

    // Listen for logout events broadcast by other tabs
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel('session_events');
      bc.onmessage = (event) => {
        if (event.data?.type === 'SESSION_LOGOUT') {
          // Another tab logged out — check if we can log in now
          checkAndLogin();
        }
      };
    } catch {
      // BroadcastChannel not supported (very old browsers) — manual refresh only
    }

    return () => {
      bc?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty — checkAndLogin reads latest data via ref

  // Redirect direct visits without device-limit context. Navigation belongs in
  // an effect; doing it during render races with the successful-login route.
  useEffect(() => {
    if (
      !sessionLimitData &&
      !isSsoPending &&
      !isCompletingFlowRef.current
    ) {
      navigate('/signin', { replace: true });
    }
  }, [sessionLimitData, isSsoPending, navigate]);

  if (!sessionLimitData && !isSsoPending) return null;

  const activeSessions = isSsoPending
    ? ssoSessions
    : sessionLimitData!.activeSessions;
  const maxSessions = isSsoPending
    ? ssoMaxSessions
    : sessionLimitData!.maxSessions;

  async function handleLogoutAndLogin(session: IActiveSession) {
    if (
      !isSsoPending &&
      isAuthEncryptedPassword(sessionLimitData?.password) &&
      captchaEnabled &&
      !captchaToken
    ) {
      setError('Please Complete Security Verification Before Continuing');
      return;
    }

    setLoadingSessionId(session.sessionId);
    setError(null);
    try {
      if (isSsoPending) {
        const result = await replacePendingSsoSession(
          session.sessionId,
        ).unwrap();
        if (result.data.completed && result.data.redirectUrl) {
          window.location.assign(result.data.redirectUrl);
          return;
        }
        await checkAndLogin();
        return;
      }
      const authPayload = buildPublicSessionAuthPayload(
        sessionLimitData.email,
        sessionLimitData.password,
      );
      await publicLogoutSession({ ...authPayload, sessionId: session.sessionId }).unwrap();
      // Complete this tab's deterministic retry before notifying other tabs.
      // Broadcasting first can trigger this component's listener and start a
      // second, competing check-and-login request.
      await checkAndLogin();

      // Broadcast so other device-limit tabs also react.
      try {
        const bc = new BroadcastChannel('session_events');
        bc.postMessage({ type: 'SESSION_LOGOUT' });
        bc.close();
      } catch { /* ignore */ }
    } catch (err: any) {
      setError(
        err?.data?.message ||
        err?.message ||
        'Something went wrong. Please try again or go back to sign in.',
      );
    } finally {
      setLoadingSessionId(null);
    }
  }

  function handleGoBack() {
    isCompletingFlowRef.current = true;
    clearSessionLimitData();
    navigate('/signin', { replace: true });
  }

  const isAnyLoading =
    isLoggingOut || isLoggingIn || isRefreshing || isReplacingSsoSession;

  return (
    <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center px-4 py-10 font-sans">
      <div className="w-full max-w-3xl">
        {/* ── Main card ── */}
        <div className="bg-[#1a1a1a] rounded-2xl overflow-hidden shadow-2xl border border-white/5">
          <div className="flex flex-col md:flex-row">

            {/* ── Left panel ── */}
            <div className="md:w-2/5 bg-[#141414] flex flex-col items-center justify-center px-8 py-10 border-b md:border-b-0 md:border-r border-white/5">
              <div className="relative w-36 h-28 mb-6">
                {/* TV / monitor */}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-32 h-20 bg-[#2a2a2a] rounded-lg border border-white/10 flex items-center justify-center">
                  <div className="w-24 h-14 bg-[#1f1f1f] rounded flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="4" strokeWidth="1.5" />
                      <path d="M12 2v2M12 20v2M2 12h2M20 12h2" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </div>
                </div>
                {/* Laptop */}
                <div className="absolute top-0 left-0 w-20 h-14 bg-[#252525] rounded-md border border-white/10 flex items-center justify-center shadow-lg">
                  <div className="w-14 h-9 bg-[#1a1a1a] rounded-sm flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-red-500/60" />
                  </div>
                </div>
                {/* Phone */}
                <div className="absolute top-2 right-0 w-10 h-16 bg-[#252525] rounded-lg border border-white/10 flex items-center justify-center shadow-lg">
                  <div className="w-6 h-10 bg-[#1a1a1a] rounded-sm" />
                </div>
              </div>

              <h2 className="text-white text-lg font-bold text-center leading-snug">
                Login Pending,<br />Device Limit Reached
              </h2>
              <p className="text-gray-400 text-xs text-center mt-2 leading-relaxed">
                Your organization supports {maxSessions} device{maxSessions > 1 ? 's' : ''} only
              </p>

              {/* Listening indicator */}
              <div className="flex items-center gap-1.5 mt-4">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                </span>
                <span className="text-gray-600 text-xs">Waiting for a device to log out…</span>
              </div>
            </div>

            {/* ── Right panel ── */}
            <div className="flex-1 px-8 py-8">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-white text-base font-semibold">
                  Log Out 1 Device to Continue
                </h3>
                {/* Manual refresh button */}
                <button
                  onClick={refreshSessions}
                  disabled={isAnyLoading}
                  title="Refresh device list"
                  className="p-1.5 rounded-lg text-gray-500 hover:text-gray-300 hover:bg-white/5 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <svg
                    className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>
              </div>
              <p className="text-gray-400 text-xs mb-6">
                Select a CConnect device below to log it out — we'll sign you in
                automatically when a slot opens. This does not sign you out of
                Keycloak, Microsoft, or other company applications.
              </p>

              {/* Captcha verification widget */}
              {!isSsoPending && captchaEnabled && (
                <div className="mb-5 p-3.5 bg-[#242424] rounded-xl border border-white/5">
                  <p className="text-white text-xs font-semibold mb-2.5">
                    Security Verification
                  </p>
                  <TurnstileCaptcha
                    ref={captchaRef}
                    action="device_limit_login"
                    onVerify={handleCaptchaVerify}
                    onExpire={handleCaptchaExpire}
                    onError={handleCaptchaError}
                  />
                </div>
              )}

              {/* Session list */}
              <div className="space-y-3">
                {activeSessions.map((session) => {
                  const isThisLoading = loadingSessionId === session.sessionId && isAnyLoading;
                  return (
                    <div
                      key={session.sessionId}
                      className="flex items-center gap-4 bg-[#242424] rounded-xl px-4 py-3 border border-white/5 hover:border-white/10 transition-colors"
                    >
                      <div className="flex-shrink-0 w-10 h-10 bg-[#2e2e2e] rounded-lg flex items-center justify-center">
                        <DeviceIcon label={session.deviceLabel} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          {session.deviceLabel}
                        </p>
                        <p className="text-gray-500 text-xs mt-0.5">
                          Last used: {relativeTime(session.lastActiveAt)}
                        </p>
                      </div>

                      <button
                        onClick={() => handleLogoutAndLogin(session)}
                        disabled={isAnyLoading}
                        className={`
                          flex-shrink-0 px-4 py-1.5 rounded-lg text-xs font-semibold
                          border transition-all duration-150
                          ${isThisLoading
                            ? 'bg-white/5 border-white/10 text-gray-500 cursor-not-allowed'
                            : 'bg-[#2e2e2e] border-white/15 text-white hover:bg-white/10 hover:border-white/25 cursor-pointer'
                          }
                        `}
                      >
                        {isThisLoading ? (
                          <span className="flex items-center gap-1.5">
                            <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                            </svg>
                            Logging out…
                          </span>
                        ) : (
                          'Log Out'
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>

              {error && (
                <div className="mt-4 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <p className="text-red-400 text-xs">{error}</p>
                </div>
              )}

              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-white/8" />
                <span className="text-gray-600 text-xs uppercase tracking-widest">or</span>
                <div className="flex-1 h-px bg-white/8" />
              </div>

              <button
                onClick={handleGoBack}
                disabled={isAnyLoading}
                className="w-full py-2.5 rounded-xl text-sm font-medium text-gray-400 border border-white/8 hover:bg-white/5 hover:text-white transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Back to Sign In
              </button>
            </div>
          </div>
        </div>

        <p className="text-center text-gray-600 text-xs mt-5">
          Need more devices?{' '}
          <span className="text-gray-400">Contact your administrator.</span>
        </p>
      </div>
    </div>
  );
}
